import React, { useState, useEffect } from 'react';
import { useDebouncedCallback } from '../../hooks/useDebounce';

import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaClock,
  FaTag,
  FaPlay,
  FaPause,
  FaCalendarAlt,
  FaFileExcel,
  FaFilter,
  FaUsers,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';

import { Pagination } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';
import { useAuth } from '../../provider/auth.provider';
import axiosInstance from '../../libs/axios';

interface Designation {
  id: number;
  name: string;
  is_active: boolean;
  show_in_reporting_person_list?: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by?: number;
  suspended_by?: number;
  suspended_at?: string;
}

interface Pagination {
  offset: number;
  limit: number;
  page: number;
  total_pages: number;
  total_count: number;
  has_next_page: boolean;
  has_prev_page: boolean;
  next_page: number | null;
  prev_page: number | null;
}

interface DesignationsListProps {
  designations: Designation[];
  pagination: Pagination;
  onSearch: (search: string) => void;
  onStatusFilter: (status: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onAddDesignation: () => void;
  onEditDesignation: (designation: Designation) => void;
  onDeleteDesignation: (designation: Designation) => void;
  onToggleStatus: (designation: Designation) => void;
  onToggleReportingPersonList: (designation: Designation, showInList: boolean) => void;
  loading?: boolean;
  onSort?: (field: string, direction: 'ASC' | 'DESC') => void;
  searchValue?: string;
  statusValue?: string;
  sortState?: { field: string; direction: 'ASC' | 'DESC' };
}

export const DesignationsList: React.FC<DesignationsListProps> = ({
  designations,
  pagination,
  onSearch,
  onStatusFilter,
  onPageChange,
  onLimitChange,
  onAddDesignation,
  onEditDesignation,
  onDeleteDesignation,
  onToggleStatus,
  onToggleReportingPersonList,
  loading = false,
  onSort,
  searchValue = '',
  statusValue = '',
  sortState
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [idFilter, setIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');
  const [updatedByFilter, setUpdatedByFilter] = useState('');
  const [suspendedByFilter, setSuspendedByFilter] = useState('');
  const [createdAtFilter, setCreatedAtFilter] = useState('');
  const [updatedAtFilter, setUpdatedAtFilter] = useState('');
  const [suspendedAtFilter, setSuspendedAtFilter] = useState('');
  const [showExcelModal, setShowExcelModal] = useState(false);
  const { hasPermission } = useAuth();
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>({
    field: 'created_at',
    direction: 'DESC'
  });
  const [exporting, setExporting] = useState(false);
  const [exportData, setExportData] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    setSearchTerm(searchValue || '');
  }, [searchValue]);

  useEffect(() => {
    setStatusFilter(statusValue || '');
  }, [statusValue]);

  useEffect(() => {
    if (sortState) {
      setSortConfig(sortState);
    }
  }, [sortState]);

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedOnSearch(value);
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setStatusFilter(value);
    onStatusFilter(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        );
      case 'SUSPENDED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Suspended
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    setSortConfig({ field, direction });
    onSort?.(field, direction);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />;
    return sortConfig.direction === 'ASC' ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />;
  };

  const getSearchParam = () => (searchTerm.trim() || searchValue.trim());

  const getStatusParam = () => (statusFilter.trim() || statusValue.trim());

  const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString() : '');

  const mapDesignationForExport = (designation: Designation) => ({
    'Designation Name': designation.name,
    Status: designation.is_active ? 'Active' : 'Suspended',
    'Allow In Reporting Person List': designation.show_in_reporting_person_list ? 'Yes' : 'No',
    'Created Date': formatDateTime(designation.created_at),
    'Updated Date': formatDateTime(designation.updated_at),
    'Suspended Date': formatDateTime(designation.suspended_at),
    // 'Updated By': designation.updated_by || 'N/A',
    // 'Suspended By': designation.suspended_by || 'N/A',
  });

  const buildExportParams = () => {
    const params: Record<string, any> = {};
    const searchParam = getSearchParam();
    if (searchParam) params.search = searchParam;

    const statusParam = getStatusParam();
    if (statusParam) params.status = statusParam;

    if (sortConfig.field) {
      params.sort_by = sortConfig.field;
      params.sort_order = sortConfig.direction;
    }

    return params;
  };

  const fetchExportData = async () => {
    try {
      setExporting(true);
      const params = buildExportParams();
      const limit = 100;
      let page = 1;
      let totalPages = 1;
      const allDesignations: Designation[] = [];

      do {
        const response = await axiosInstance.get('/designations/list', {
          params: {
            ...params,
            page,
            limit,
          },
        });

        const responseData = response.data?.data;
        const pageDesignations: Designation[] = responseData?.designations || [];
        allDesignations.push(...pageDesignations);

        const paginationInfo = responseData?.pagination;
        if (paginationInfo) {
          totalPages =
            paginationInfo.total_pages ??
            paginationInfo.totalPages ??
            (paginationInfo.total_count ? Math.ceil(paginationInfo.total_count / limit) : totalPages);
        } else if (pageDesignations.length < limit) {
          totalPages = page;
        }

        page += 1;
      } while (page <= totalPages);

      setExportData(allDesignations.map(mapDesignationForExport));
      setShowExcelModal(true);
    } catch (error) {
      console.error('Error exporting designations:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Designations Management</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? 'Preparing...' : 'Export Excel'}
            </button>

            {hasPermission('Designation', 'create') && (
              <button
                onClick={onAddDesignation}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add New Designation
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search Designations</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search by designation name"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Status Filter</label>
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Actions
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Designation Name
                  {getSortIcon('name')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('is_active')}
              >
                <div className="flex items-center gap-1">
                  Status
                  {getSortIcon('is_active')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[220px]">
                Allow In Reporting Person List
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  Created At
                  {getSortIcon('created_at')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('suspended_at')}
              >
                <div className="flex items-center gap-1">
                  Suspended At
                  {getSortIcon('suspended_at')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading designations...</span>
                  </div>
                </td>
              </tr>
            ) : designations.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <p className="text-sm text-gray-500">No designations found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new designation</p>
                  </div>
                </td>
              </tr>
            ) : (
              designations.map((designation) => (
                <tr key={designation.id} className="hover:bg-gray-50 transition-colors">
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {hasPermission('Designation', 'edit') && (
                        <button
                          onClick={() => onEditDesignation(designation)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="Edit designation"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {/* <button
                        onClick={() => onToggleStatus(designation)}
                        className={`p-2 rounded transition-colors ${
                          designation.status === 'ACTIVE'
                            ? 'text-red-600 hover:text-red-800 hover:bg-red-50'
                            : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                        }`}
                        title={designation.status === 'ACTIVE' ? 'Suspend designation' : 'Activate designation'}
                      >
                        {designation.status === 'ACTIVE' ? (
                          <FaPause className="w-4 h-4" />
                        ) : (
                          <FaPlay className="w-4 h-4" />
                        )}
                      </button> */}
                      {
                        // hasPermission('Designation', 'delete') &&
                        // <button
                        //   onClick={() => onDeleteDesignation(designation)}
                        //   className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                        //   title="Delete designation"
                        // >
                        //   <FaTrash className="w-4 h-4" />
                        // </button>
                      }
                    </div>
                  </td>

                  {/* Designation Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {designation.name}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {designation.is_active
                            ? (<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>)
                            : (<span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Suspended
                            </span>)
                          }
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <label className={`inline-flex items-center ${hasPermission('Designation', 'edit') ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={Boolean(designation.show_in_reporting_person_list)}
                          disabled={loading || !hasPermission('Designation', 'edit')}
                          onChange={(e) => onToggleReportingPersonList(designation, e.target.checked)}
                        />
                        <span className="relative h-5 w-10 rounded-full bg-gray-300 transition-colors peer-checked:bg-blue-700 peer-disabled:opacity-60 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
                      </label>
                      <span className={`text-xs font-medium ${designation.show_in_reporting_person_list ? 'text-green-700' : 'text-gray-500'}`}>
                        {designation.show_in_reporting_person_list ? 'Allowed' : 'Not Allowed'}
                      </span>
                    </div>
                  </td>

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <span>{formatDate(designation.created_at)}</span>
                    </div>
                  </td>

                  {/* Suspended At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {designation.suspended_at ? formatDate(designation.suspended_at) : '-'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        pagination={pagination}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="designations"
        title="Export Designations Data"
        description="Download the filtered designations with all available details"
      />
    </div >
  );
};
